import { createFileRoute, Link } from "@tanstack/react-router"
import {
  ArrowRight,
  ChartNoAxesCombined,
  CircleCheck,
  Layers,
  ShieldCheck,
  Star,
  Zap,
} from "lucide-react"

import ConsumerNav from "@/components/homepage/consumer-nav"
import { LocationSearchForm } from "@/components/homepage/location-search-form"
import { SharedFooter } from "@/components/shared-footer"
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card"
import { stateSlugByCode } from "@/constants/state"

const MARKETS = [
  {
    stateCode: "CA",
    slug: "greater-los-angeles",
    city: "Greater Los Angeles",
    certified: 12,
  },
  {
    stateCode: "CA",
    slug: "greater-san-diego",
    city: "Greater San Diego",
    certified: 18,
  },
  {
    stateCode: "CA",
    slug: "inland-empire",
    city: "Inland Empire",
    certified: 23,
  },
  {
    stateCode: "CA",
    slug: "east-bay",
    city: "East Bay",
    certified: 8,
  },
  {
    stateCode: "CA",
    slug: "south-bay-silicon-valley",
    city: "South Bay",
    certified: 15,
  },
  {
    stateCode: "CA",
    slug: "north-bay",
    city: "North Bay",
    certified: 9,
  },
  {
    stateCode: "CA",
    slug: "sacramento-metro",
    city: "Sacramento Metro",
    certified: 11,
  },
  {
    stateCode: "CA",
    slug: "san-francisco",
    city: "San Francisco",
    certified: 6,
  },
] as const

export const Route = createFileRoute("/")({
  component: HomePage,
  head: () => ({
    meta: [
      { title: "WattBot — Find the Best Solar Installers" },
      {
        name: "description",
        content:
          "We analyzed 20,000+ solar companies so you know who to trust. Independent ratings, real data, no bias.",
      },
    ],
  }),
})

function HomePage() {
  return (
    <div className="bg-muted min-h-screen p-4 sm:p-6 lg:p-8">
      <main className="bg-background mx-auto max-w-[1400px] border-2 shadow-md">
        <ConsumerNav />
        {/* ── HERO ── */}
        <section className="grid grid-cols-1 border-b-2 lg:grid-cols-12">
          {/* Left column */}
          <div className="relative flex min-h-[400px] flex-col justify-center overflow-hidden p-8 md:min-h-[600px] md:p-16 lg:col-span-8 lg:p-20">
            <div className="pattern-grid pointer-events-none absolute inset-0 opacity-20" />
            <div className="relative z-10 max-w-3xl">
              <div className="wb-tag bg-foreground text-background mb-6 inline-block -rotate-1 px-3 py-1 font-mono">
                {`Independent Analysis \u00B7 100% Unbiased`}
              </div>
              <h1 className="wb-hero">
                {`DON'T GET `}
                <span className="relative inline-block">
                  BURNED
                  <svg
                    className="absolute -bottom-2 left-0 h-3 w-full"
                    viewBox="0 0 100 10"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M0 5 Q 50 10 100 5"
                      stroke="hsl(var(--brand))"
                      strokeWidth="8"
                      fill="none"
                    />
                  </svg>
                </span>
                {` BY SOLAR.`}
              </h1>
              <p className="text-muted-foreground my-10 max-w-xl font-mono text-xl md:text-2xl">
                {`We analyzed 20,000+ companies so you know who to trust.`}
              </p>
              <div className="max-w-xl">
                <LocationSearchForm />
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="brand-light flex flex-col border-t-2 lg:col-span-4 lg:border-t-0 lg:border-l-2">
            <div className="relative flex flex-grow flex-col items-center justify-center overflow-hidden p-8 text-center">
              <div className="pattern-stripe pointer-events-none absolute inset-0 opacity-10" />
              {/* Trust Score card */}
              <div className="relative z-10 mb-8 max-w-[280px] rotate-2 border-2 bg-white p-6 text-black shadow-md">
                <div className="mb-4 border-b border-dashed border-black pb-2 text-sm font-bold uppercase">
                  Trust Score
                </div>
                <div className="text-7xl font-black tracking-tighter">94</div>
                <div className="text-muted-foreground mt-2 font-mono text-xs uppercase">
                  Top 1% in your area
                </div>
              </div>

              {/* Typical Installer card */}
              <div className="relative z-10 max-w-[280px] -rotate-1 border-2 bg-white p-6 text-black opacity-50 shadow-md grayscale">
                <div className="mb-4 border-b border-dashed border-black pb-2 text-sm font-bold uppercase">
                  Typical Installer
                </div>
                <div className="text-7xl font-black tracking-tighter text-gray-400">
                  42
                </div>
                <div className="mt-2 font-mono text-xs text-gray-500 uppercase">
                  Warning: Risky
                </div>
              </div>
            </div>

            {/* Stats bar inside hero right column */}
            <div className="brand-dark border-t-2 p-6">
              <div className="mb-2 font-mono text-xs text-gray-400 uppercase">
                Live Database Stats
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-2xl font-bold">21,402</div>
                  <div className="text-[10px] tracking-wider text-gray-400 uppercase">
                    Companies
                  </div>
                </div>

                <div>
                  <div className="text-2xl font-bold">11,221</div>
                  <div className="text-[10px] tracking-wider text-gray-400 uppercase">
                    Zip Codes
                  </div>
                </div>
                <div>
                  <div className="text-2xl font-bold">130M+</div>
                  <div className="text-[10px] tracking-wider text-gray-400 uppercase">
                    Data Points
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── VALUE PROPS ── */}
        <section className="border-b-2">
          <div className="grid divide-y md:grid-cols-3 md:divide-x md:divide-y-0">
            {[
              {
                icon: CircleCheck,
                title: "ELITE",
                description: "Only 5% of companies make the cut.",
              },
              {
                icon: ChartNoAxesCombined,
                title: "Independent",
                description: `Our verdicts & rankings are not for sale.`,
              },
              {
                icon: Zap,
                title: "REAL-TIME",
                description: "Major platforms continually monitored.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="flex items-center gap-4 px-6 py-6"
              >
                <div className="bg-foreground text-background flex size-12 shrink-0 items-center justify-center">
                  <item.icon className="size-6" />
                </div>
                <div>
                  <div className="wb-h4">{item.title}</div>
                  <p className="text-muted-foreground font-mono text-sm">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── THE PROBLEM + THE WATTBOT DIFFERENCE ── */}
        <section
          id="why-it-matters"
          className="grid scroll-mt-24 grid-cols-1 border-b-2 lg:min-h-[600px] lg:grid-cols-2"
        >
          {/* Left: The Problem */}
          <div className="bg-muted relative flex flex-col justify-center border-b-2 p-10 pt-16 pb-14 lg:border-r-2 lg:border-b-0 lg:p-16">
            <div className="wb-tag bg-foreground text-background absolute top-0 left-0 px-4 py-2">
              The Problem
            </div>
            <h2 className="mb-8 text-3xl leading-none font-bold uppercase md:text-4xl lg:text-5xl">
              WHY
              <span className="bg-brand inline-block -skew-x-6 px-2">
                STAR RATINGS
              </span>
              ARE NOT ENOUGH
            </h2>
            <p className="mb-10 max-w-md font-mono">
              {`81% of solar reviews are 5 stars. 74% of solar companies average
              4.5+ stars. When everyone is "excellent", you need to dig
              deeper.`}
            </p>

            {/* Mock review card */}
            <div className="max-w-sm rotate-1 border-2 bg-white p-6 text-black shadow-md">
              <div className="mb-2 flex items-center gap-3">
                <div className="flex size-8 items-center justify-center rounded-full bg-blue-500 text-sm font-bold text-white">
                  G
                </div>
                <span className="font-bold">Sunshine Solar Co.</span>
              </div>
              <div className="mb-3 flex gap-0.5 text-xl text-yellow-500">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="size-5 fill-current" />
                ))}
              </div>
              <p className="text-muted-foreground mb-4 text-sm italic">
                {`"Great sales team!" - 1 week after signing`}
              </p>
              <div className="border-bad-foreground bg-bad border px-4 py-3">
                <span className="text-bad-foreground text-xs font-bold uppercase">
                  REALITY CHECK:
                </span>{" "}
                <span className="text-bad-foreground text-sm">
                  Permit denied. Project stalled for 8 months.
                </span>
              </div>
            </div>
          </div>

          {/* Right: The WattBot Difference */}
          <div className="pattern-dot relative flex flex-col justify-center p-10 lg:p-16">
            <div className="wb-tag bg-brand text-brand-foreground absolute top-0 left-0 border-r-2 border-b-2 px-4 py-2">
              The WattBot Difference
            </div>

            <div className="mx-auto mt-6 max-w-md space-y-8">
              {[
                {
                  title: "WORK QUALITY",
                  description:
                    "We analyze sales conduct, workmanship, project management, after-sales support and value for money.",
                },
                {
                  title: "LONG-TERM SATISFACTION",
                  description:
                    "We track satisfaction 1, 3 and 5 years after completion.",
                },
                {
                  title: "BEYOND REVIEWS",
                  description:
                    "We check licenses, company standing, business history and performance trends.",
                },
                {
                  title: "LOCAL COMPARISON",
                  description:
                    "We rank every installer against every other installer in your market.",
                },
              ].map((item, idx) => (
                <div key={item.title}>
                  <div className="flex items-start gap-4">
                    <div className="bg-foreground text-background flex size-12 shrink-0 items-center justify-center font-mono font-bold">
                      VS
                    </div>
                    <div>
                      <h3 className="mb-2 text-lg font-bold uppercase">
                        {item.title}
                      </h3>
                      <p className="text-muted-foreground font-mono">
                        {item.description}
                      </p>
                    </div>
                  </div>
                  {idx < 3 && <div className="mt-8 h-px w-full bg-gray-200" />}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section
          id="how-it-works"
          className="brand-light relative scroll-mt-24 border-b-2"
        >
          <div className="wb-tag bg-brand text-brand-foreground absolute top-0 left-0 border-r-2 border-b-2 px-4 py-2">
            How It Works
          </div>
          <div className="grid divide-y-2 md:grid-cols-3 md:divide-x-2 md:divide-y-0">
            {[
              {
                num: "01",
                icon: Layers,
                title: "DATA REVIEW",
                description:
                  "Our team reviews Google, Yelp, BBB, SolarReviews, EnergySage, public permit records, and government license databases.",
              },
              {
                num: "02",
                icon: ShieldCheck,
                title: "AI ANALYSIS",
                description:
                  "Our algorithms identify fake reviews, patterns of delays, hidden fees, and poor workmanship.",
              },
              {
                num: "03",
                icon: CircleCheck,
                title: "TRUST SCORE",
                description:
                  "You get a single, unbiased score from 0-100. No fluff. Just the probability of a good experience.",
              },
            ].map((item) => (
              <div
                key={item.num}
                className="relative px-6 pt-14 pb-8 first:pt-16 md:px-10 md:py-14 md:first:pt-14"
              >
                <div className="pointer-events-none absolute top-4 right-4 text-6xl font-black opacity-10">
                  {item.num}
                </div>
                <div className="relative z-10">
                  <div className="bg-background mb-6 flex size-16 items-center justify-center border-2 shadow-sm">
                    <item.icon className="size-8" />
                  </div>
                  <h3 className="wb-h4 mt-6 text-base">{item.title}</h3>
                  <p className="mt-2 font-mono text-sm leading-relaxed opacity-80">
                    {item.description}
                  </p>
                  {item.num === "03" && (
                    <Link
                      to="/methodology"
                      className="mt-4 inline-block font-mono text-sm font-bold underline underline-offset-4"
                    >
                      {`Read our full methodology \u2192`}
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── TOP MARKETS ── */}
        <section id="markets" className="scroll-mt-24 border-b-2 p-8 md:p-16">
          <div className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="mb-4 text-3xl font-bold tracking-tighter uppercase md:text-4xl">
                Top Markets
              </h2>
              <p className="max-w-xl font-mono text-gray-500">
                {`We are currently tracking installer performance nationally. These are our top markets in California.`}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {MARKETS.map((market) => (
              <Link
                key={market.slug}
                to="/solar-installers/$state/$marketSlug/$"
                params={{
                  state: stateSlugByCode[market.stateCode],
                  marketSlug: market.slug,
                  _splat: "",
                }}
                className="block"
              >
                <Card isInteractive className="group flex h-full flex-col">
                  <CardHeader className="flex-row items-start justify-between pb-8">
                    <div className="wb-data-muted">{market.stateCode}</div>
                    <div className="bg-brand size-2 rounded-full" />
                  </CardHeader>
                  <CardContent className="flex-grow pb-0">
                    <h3 className="mb-2 text-2xl font-bold uppercase group-hover:underline group-hover:decoration-2 group-hover:underline-offset-4">
                      {market.city}
                    </h3>
                  </CardContent>
                  <CardFooter>
                    <span className="wb-tag">{market.certified} Certified</span>
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                  </CardFooter>
                </Card>
              </Link>
            ))}
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="bg-muted relative overflow-hidden p-8 text-center md:p-20">
          <div className="pattern-grid pointer-events-none absolute inset-0 opacity-10" />
          <div className="relative z-10 mx-auto max-w-2xl">
            <h2 className="mb-6 text-3xl font-black tracking-tighter uppercase md:text-5xl">
              {`Ready to see the `}
              <span className="bg-brand px-2">{`truth?`}</span>
            </h2>
            <p className="text-muted-foreground mb-8 font-mono">
              Most installers look great online. Find the ones you can trust.
            </p>
            <div className="mx-auto max-w-xl">
              <LocationSearchForm />
            </div>
          </div>
        </section>

        {/* ── FOOTER ── */}
        <SharedFooter />
      </main>
    </div>
  )
}
